import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, CalendarCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDDEN_ON = ["/admin", "/barber-live"];
const CHIPS = ["Next opening?", "Book a cut", "Check availability", "Pop-up schedule"];

const getSessionId = () => {
  let id = localStorage.getItem("lxg_concierge_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("lxg_concierge_session", id);
  }
  return id;
};

export const ConciergeWidget = () => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [toolHint, setToolHint] = useState("");
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, toolHint]);

  if (HIDDEN_ON.some((p) => pathname.startsWith(p))) return null;

  const send = async (text) => {
    const message = (text ?? input).trim();
    if (!message || busy) return;
    setInput("");
    setBusy(true);
    setMessages((ms) => [...ms, { role: "user", text: message }, { role: "assistant", text: "", pending: true }]);
    try {
      const res = await fetch(`${API}/concierge/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: getSessionId(), message }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let idx;
        while ((idx = buf.indexOf("\n\n")) >= 0) {
          const chunk = buf.slice(0, idx);
          buf = buf.slice(idx + 2);
          if (!chunk.startsWith("data:")) continue;
          let ev;
          try {
            ev = JSON.parse(chunk.slice(5));
          } catch {
            continue;
          }
          if (ev.type === "delta") {
            setToolHint("");
            setMessages((ms) => {
              const copy = [...ms];
              const last = copy[copy.length - 1];
              copy[copy.length - 1] = { ...last, text: last.text + ev.content, pending: false };
              return copy;
            });
          } else if (ev.type === "tool") {
            setToolHint(ev.name === "check_availability" || ev.name === "next_openings" ? "Checking the books…" : "Saving your request…");
          } else if (ev.type === "openings") {
            setMessages((ms) => {
              const copy = [...ms];
              copy[copy.length - 1] = { ...copy[copy.length - 1], openings: ev.items };
              return copy;
            });
          } else if (ev.type === "error") {
            setMessages((ms) => {
              const copy = [...ms];
              copy[copy.length - 1] = { role: "assistant", text: ev.detail, pending: false };
              return copy;
            });
          }
        }
      }
    } catch {
      setMessages((ms) => {
        const copy = [...ms];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "The concierge is momentarily unavailable — please try again, or reach us at ltaplett@luxgroom.net.",
          pending: false,
        };
        return copy;
      });
    } finally {
      setToolHint("");
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="concierge-bubble-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Chat with the LUXGROOM concierge"
        className="fixed bottom-6 right-6 z-[60] h-14 w-14 rounded-full bg-gold text-black flex items-center justify-center shadow-[0_0_30px_rgba(201,163,78,0.35)] hover:bg-gold-light hover:scale-105 transition-all"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="concierge-panel"
            initial={{ opacity: 0, y: 24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.97 }}
            transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-24 right-6 z-[60] w-[min(92vw,400px)] h-[min(70vh,560px)] flex flex-col border border-gold/30 bg-[#0c0c0c]/95 backdrop-blur-xl shadow-2xl"
          >
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <div>
                <p className="text-[10px] uppercase tracking-[0.3em] text-gold">LUXGROOM</p>
                <p className="font-display text-lg text-[#FDFBF7]">Concierge</p>
              </div>
              <span className="flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-white/40">
                <span className="h-1.5 w-1.5 rounded-full bg-gold animate-pulse" /> Online
              </span>
            </div>
            <div ref={listRef} data-testid="concierge-message-list" className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-4">
                  <p className="text-sm text-white/70 leading-relaxed">
                    Good to see you. Ask me about services, pop-up days, or open times — I can take
                    your booking request right here.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {CHIPS.map((c) => (
                      <button
                        key={c}
                        data-testid={`concierge-chip-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                        onClick={() => send(c)}
                        className="text-xs px-4 py-2 border border-gold/40 text-gold hover:bg-gold hover:text-black transition-colors"
                      >
                        {c}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} data-testid={`concierge-message-${m.role}-${i}`} className={m.role === "user" ? "text-right" : "text-left"}>
                  <span
                    className={`inline-block max-w-[85%] px-4 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                      m.role === "user"
                        ? "bg-gold text-black"
                        : "bg-white/5 border border-white/10 text-white/85"
                    }`}
                  >
                    {m.text}
                    {m.pending && <span className="inline-block h-3 w-1 ml-1 bg-gold/70 animate-pulse" />}
                  </span>
                  {m.openings?.length > 0 && (
                    <div data-testid={`concierge-openings-${i}`} className="mt-2 flex flex-wrap gap-2">
                      {m.openings.map((o, j) => (
                        <button
                          key={`${o.date}-${o.time}`}
                          type="button"
                          data-testid={`concierge-opening-${i}-${j}`}
                          onClick={() => { setOpen(false); navigate(`/offer?date=${o.date}&time=${encodeURIComponent(o.time)}`); }}
                          className="inline-flex items-center gap-2 text-xs px-4 py-2 border border-gold/50 text-gold hover:bg-gold hover:text-black transition-colors"
                        >
                          <CalendarCheck size={12} /> {o.short} · Book
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {toolHint && (
                <p data-testid="concierge-tool-hint" className="text-[11px] uppercase tracking-[0.2em] text-gold/70 animate-pulse">
                  {toolHint}
                </p>
              )}
            </div>
            <form
              data-testid="concierge-form"
              onSubmit={(e) => {
                e.preventDefault();
                send();
              }}
              className="border-t border-white/10 p-3 flex gap-2"
            >
              <input
                data-testid="concierge-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask the concierge…"
                maxLength={2000}
                className="flex-1 bg-transparent px-3 py-2 text-sm text-[#FDFBF7] placeholder:text-white/30 focus:outline-none"
              />
              <button
                data-testid="concierge-send-button"
                type="submit"
                disabled={busy || !input.trim()}
                aria-label="Send message"
                className="h-10 w-10 flex items-center justify-center bg-gold text-black hover:bg-gold-light transition-colors disabled:opacity-40"
              >
                <Send size={16} />
              </button>
            </form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
